import React, { useState, useEffect } from 'react';
import config from '../config';

// Helper to get theme-aware colors
const getThemeColors = () => {
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  
  return {
    primary: isDark ? '#e8e8e8' : '#223651',
    secondary: isDark ? '#a0a0a0' : '#52555b',
    border: isDark ? '#4a4a4a' : '#e5e7eb',
    gridLine: isDark ? '#404040' : '#f1f5f9',
    text: isDark ? '#e8e8e8' : '#374151',
    textMuted: isDark ? '#a0a0a0' : '#6b7280',
    background: isDark ? '#1a1a1a' : '#ffffff',
    backgroundAlt: isDark ? '#2d2d2d' : '#f8f9fa',
    warning: isDark ? '#d97706' : '#f68c1e',
    // Score card colors
    scoreLow: { bg: isDark ? '#3a1a1a' : '#fef2f2', border: isDark ? '#6a1a1a' : '#fecaca', text: isDark ? '#f87171' : '#dc2626' },
    scoreMed: { bg: isDark ? '#3a3a1a' : '#fffbeb', border: isDark ? '#6a6a1a' : '#fed7aa', text: isDark ? '#f0a500' : '#d97706' },
    scoreHigh: { bg: isDark ? '#1a3a1a' : '#f0fdf4', border: isDark ? '#2a5a2a' : '#bbf7d0', text: isDark ? '#4ade80' : '#16a34a' }
  };
};

// Simple chart components (without Chart.js dependencies)
const SimpleLineChart = ({ data, title, themeColors }) => {
  const colors = themeColors || getThemeColors();
  if (!data || data.labels.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No data available for {title}
      </div>
    );
  }

  const maxValue = 5;
  const chartHeight = 200;
  const bottomPadding = 50; // Increased space for long rotated date labels
  const labelWidth = 40; // Reduced for Y-axis labels only
  const innerWidth = 600;
  const rightPadding = 80;
  const chartWidth = labelWidth + innerWidth + rightPadding;
  const totalHeight = chartHeight + bottomPadding;

  return (
    <div style={{ width: '100%', overflow: 'visible' }}>
      <svg width="100%" viewBox={`0 0 ${chartWidth} ${totalHeight}`} preserveAspectRatio="xMinYMin meet" style={{ display: 'block' }}>
        {/* Grid lines */}
        {[1, 2, 3, 4, 5].map(y => (
          <line
            key={y}
            x1={labelWidth}
            y1={chartHeight - 10 - (y * (chartHeight - 20) / maxValue)}
            x2={labelWidth + innerWidth}
            y2={chartHeight - 10 - (y * (chartHeight - 20) / maxValue)}
            stroke={colors.border}
            strokeDasharray="3 3"
          />
        ))}
        
        {/* Y-axis labels */}
        {[1, 2, 3, 4, 5].map(y => (
          <text
            key={y}
            x={labelWidth - 10}
            y={chartHeight - 10 - (y * (chartHeight - 20) / maxValue) + 5}
            textAnchor="end"
            fontSize="12"
            fill={colors.textMuted}
            transform={`translate(0, 10)`}
          >
            {y}
          </text>
        ))}

        {/* Line */}
        {data.datasets[0].data.map((value, index) => {
          if (index === 0) return null;
          
          const x = labelWidth + (index * innerWidth / Math.max(1, data.labels.length - 1));
          const y = chartHeight - 10 - (value * (chartHeight - 20) / maxValue);
          const prevX = labelWidth + ((index - 1) * innerWidth / Math.max(1, data.labels.length - 1));
          const prevY = chartHeight - 10 - (data.datasets[0].data[index - 1] * (chartHeight - 20) / maxValue);
          
          return (
            <line
              key={index}
              x1={prevX}
              y1={prevY}
              x2={x}
              y2={y}
              stroke="rgb(75, 192, 192)"
              strokeWidth="2"
            />
          );
        })}

        {/* Points */}
        {data.datasets[0].data.map((value, index) => {
          const x = labelWidth + (index * innerWidth / Math.max(1, data.labels.length - 1));
          const y = chartHeight - 10 - (value * (chartHeight - 20) / maxValue);
          
          
          return (
            <circle
              key={index}
              cx={x}
              cy={y}
              r="4"
              fill="rgb(75, 192, 192)"
            />
          );
        })}

        {/* X-axis labels */}
        {data.labels.map((label, index) => {
          const x = labelWidth + (index * innerWidth / Math.max(1, data.labels.length - 1));
          // Remove year from date label (e.g., "2024-09-30" -> "09-30")
          const shortLabel = label.includes('-') ? label.split('-').slice(1).join('-') : label;
          return (
            <text
              key={index}
              x={x}
              y={chartHeight + 2}
              textAnchor="middle"
              fontSize="10"
              fill="#374151"
              transform={`rotate(-45 ${x} ${chartHeight + 2})`}
            >
              {shortLabel}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

const SimpleBarChart = ({ data, title, themeColors }) => {
  const colors = themeColors || getThemeColors();
  if (!data || data.labels.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No data available for {title}
      </div>
    );
  }

  const maxValue = 5;
  const barHeight = 16;
  const spacing = 12;
  const labelWidth = 200;
  const innerWidth = 600; // reduced inner plot width to make bars narrower
  const rightPadding = 80; // extra space for value labels and border
  const chartWidth = labelWidth + innerWidth + rightPadding; // include padding area used in math
  const chartHeight = data.labels.length * (barHeight + spacing) + 30;

  return (
    <div style={{ width: '100%', overflow: 'visible' }}>
      <svg width="100%" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMinYMin meet" style={{ display: 'block' }}>
        {/* Grid lines */}
        {[1, 2, 3, 4, 5].map(x => (
          <line
            key={x}
            x1={labelWidth + (x * (innerWidth) / maxValue)}
            y1={10}
            x2={labelWidth + (x * (innerWidth) / maxValue)}
            y2={chartHeight - 10}
            stroke={colors.border}
            strokeDasharray="3 3"
          />
        ))}
        
        {/* X-axis labels */}
        {[1, 2, 3, 4, 5].map(x => (
          <text
            key={x}
            x={labelWidth + (x * (innerWidth) / maxValue)}
            y={chartHeight - 2}
            textAnchor="middle"
            fontSize="14"
            fill={colors.textMuted}
            fontWeight="500"
          >
            {x}
          </text>
        ))}

        {/* Horizontal bars */}
        {data.labels.map((label, index) => {
          const value = data.datasets[0].data[index];
          const barWidth = (value / maxValue) * (innerWidth);
          const y = 10 + index * (barHeight + spacing);
          
          return (
            <g key={index}>
              {/* Category label */}
              <text
                x={labelWidth - 10}
                y={y + barHeight / 2 + 5}
                textAnchor="end"
                fontSize="12"
                fill={colors.primary}
                fontWeight="600"
              >
                {label}
              </text>
              
              {/* Bar background */}
              <rect
                x={labelWidth}
                y={y}
                width={innerWidth}
                height={barHeight}
                fill={colors.gridLine}
                rx="6"
              />
              
              {/* Bar fill */}
              <rect
                x={labelWidth}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={data.datasets[0].backgroundColor[index]}
                rx="6"
              />
              
              {/* Value label */}
              <text
                x={labelWidth + barWidth + 10}
                y={y + barHeight / 2 + 5}
                fontSize="13"
                fill={colors.secondary}
                fontWeight="700"
              >
                {value.toFixed(1)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// Horizontal bar chart: labels are sessions, single dataset
const SimpleHorizontalBarChart = ({ data, title, themeColors }) => {
  const colors = themeColors || getThemeColors();
  if (!data || !data.labels || data.labels.length === 0 || !data.datasets || data.datasets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No data available for {title}
      </div>
    );
  }

  const maxValue = 5;
  const barHeight = 16;
  const spacing = 12;
  const labelWidth = 200;
  const innerWidth = 600; // reduced inner plot width to make bars narrower
  const rightPadding = 80; // extra space for value labels and border
  const chartWidth = labelWidth + innerWidth + rightPadding;
  const chartHeight = data.labels.length * (barHeight + spacing) + 30;

  return (
    <div style={{ width: '100%', overflow: 'visible' }}>
      <svg width="100%" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMinYMin meet" style={{ display: 'block' }}>
        {/* Grid lines */}
        {[1, 2, 3, 4, 5].map(x => (
          <line
            key={x}
            x1={labelWidth + (x * innerWidth / maxValue)}
            y1={10}
            x2={labelWidth + (x * innerWidth / maxValue)}
            y2={chartHeight - 10}
            stroke={colors.border}
            strokeDasharray="3 3"
          />
        ))}
        
        {/* X-axis labels */}
        {[1, 2, 3, 4, 5].map(x => (
          <text
            key={x}
            x={labelWidth + (x * innerWidth / maxValue)}
            y={chartHeight - 2}
            textAnchor="middle"
            fontSize="14"
            fill={colors.textMuted}
            fontWeight="500"
          >
            {x}
          </text>
        ))}

        {/* Horizontal bars */}
        {data.labels.map((session, index) => {
          const value = data.datasets[0].data[index];
          const barWidth = (value / maxValue) * innerWidth;
          const y = 10 + index * (barHeight + spacing);
          
          return (
            <g key={index}>
              {/* Session label with wrapping */}
              <foreignObject
                x={10}
                y={y - 5}
                width={labelWidth - 20}
                height={barHeight + 10}
              >
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: colors.primary,
                  textAlign: 'right',
                  lineHeight: '1.2',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end'
                }}>
                  {session}
                </div>
              </foreignObject>
              
              {/* Bar background */}
              <rect
                x={labelWidth}
                y={y}
                width={innerWidth}
                height={barHeight}
                fill={colors.gridLine}
                rx="6"
              />
              
              {/* Bar fill */}
              <rect
                x={labelWidth}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={Array.isArray(data.datasets[0].backgroundColor) 
                  ? data.datasets[0].backgroundColor[index] 
                  : data.datasets[0].backgroundColor}
                rx="6"
              />
              
              {/* Value label */}
              <text
                x={labelWidth + barWidth + 10}
                y={y + barHeight / 2 + 5}
                fontSize="13"
                fill={colors.secondary}
                fontWeight="700"
              >
                {value.toFixed(1)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const App = () => {
  // --- Real Data from API ---
  const [allProjects, setAllProjects] = useState([]);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [allResponses, setAllResponses] = useState([]);
  // Setters used to populate state from API responses; values aren't read in
  // render today, so use array-hole destructuring to silence unused-vars.
  const [, setAllPrograms] = useState([]);
  const [, setAllAccountTeams] = useState([]);
  const [allCycles, setAllCycles] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setResponseRate] = useState(0);
  const [filteredResponseRate, setFilteredResponseRate] = useState(0);

  // --- State and Logic ---
  const [filters, setFilters] = useState({ project: '', program: '', am: '', pm: '', account_team: '', workstream: '', cycle_number: '' });
  const [filteredData, setFilteredData] = useState({ submissions: [], responses: [] });
  const [activeTab, setActiveTab] = useState('overview');
  const [themeColors, setThemeColors] = useState(getThemeColors());

  // Listen for theme changes
  useEffect(() => {
    const updateTheme = () => {
      setThemeColors(getThemeColors());
    };

    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        

        // Fetch programs
        console.log('Fetching programs...');
        const programsResponse = await fetch(`${config.api.baseUrl}/cycle-planning/programs`, {
          credentials: 'include'
        });
        if (!programsResponse.ok) {
          throw new Error(`Programs fetch failed: ${programsResponse.status} ${programsResponse.statusText}`);
        }
        const programsData = await programsResponse.json();
        console.log('Programs data:', programsData);
        setAllPrograms(programsData.programs || []);

        // Fetch account teams
        console.log('Fetching account teams...');
        const accountTeamsResponse = await fetch(`${config.api.baseUrl}/cycle-planning/account-teams`, {
          credentials: 'include'
        });
        if (!accountTeamsResponse.ok) {
          throw new Error(`Account teams fetch failed: ${accountTeamsResponse.status} ${accountTeamsResponse.statusText}`);
        }
        const accountTeamsData = await accountTeamsResponse.json();
        console.log('Account teams data:', accountTeamsData);
        setAllAccountTeams(accountTeamsData.teams || []);

        // Fetch cycle plannings
        console.log('Fetching cycle plannings...');
        const cyclePlanningsResponse = await fetch(`${config.api.baseUrl}/cycle-planning/cp_session`, {
          credentials: 'include'
        });
        if (!cyclePlanningsResponse.ok) {
          throw new Error(`Cycle plannings fetch failed: ${cyclePlanningsResponse.status} ${cyclePlanningsResponse.statusText}`);
        }
        const cyclePlanningsData = await cyclePlanningsResponse.json();
        console.log('Cycle plannings data:', cyclePlanningsData);
        const projects = cyclePlanningsData.projects || [];
        console.log('Sample project with AM/PM:', projects[0]); // Debug: show first project's AM/PM data
        setAllProjects(projects);
        setFilteredProjects(projects); // Initialize filtered projects with all projects

        // Fetch submissions
        console.log('Fetching submissions...');
        const submissionsResponse = await fetch(`${config.api.baseUrl}/cycle-planning/submissions`, {
          credentials: 'include'
        });
        if (!submissionsResponse.ok) {
          throw new Error(`Submissions fetch failed: ${submissionsResponse.status} ${submissionsResponse.statusText}`);
        }
        const submissionsData = await submissionsResponse.json();
        console.log('Submissions data:', submissionsData);
        // setAllSubmissions will be set in the cycle data section

        // Fetch all survey responses in a single batch request
        try {
          const responsesResponse = await fetch(`${config.api.baseUrl}/cycle-planning/responses/all`, {
            credentials: 'include'
          });
          if (!responsesResponse.ok) {
            throw new Error(`Responses fetch failed: ${responsesResponse.status} ${responsesResponse.statusText}`);
          }
          const responsesData = await responsesResponse.json();
          const allResponses = responsesData.responses || [];
          setAllResponses(allResponses);
        } catch (err) {
          console.error('Error fetching all responses:', err);
          // Fallback: try individual requests if batch fails (with rate limiting)
          console.log('Falling back to individual requests...');
          const allResponses = [];
          const submissions = submissionsData.submissions || [];
          const batchSize = 10; // Process 10 at a time
          
          for (let i = 0; i < submissions.length; i += batchSize) {
            const batch = submissions.slice(i, i + batchSize);
            await Promise.all(batch.map(async (submission) => {
              try {
                const response = await fetch(`${config.api.baseUrl}/cycle-planning/submissions/${submission.submission_id}/responses`, {
                  credentials: 'include'
                });
                const responseData = await response.json();
                if (responseData.responses && responseData.responses.length > 0) {
                  allResponses.push(...responseData.responses);
                }
              } catch (err) {
                console.error(`Error fetching responses for ${submission.submission_id}:`, err);
              }
            }));
            
            // Small delay between batches to avoid rate limiting
            if (i + batchSize < submissions.length) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }
          console.log('Total responses fetched (fallback):', allResponses.length);
          setAllResponses(allResponses);
        }

        // Fetch response rate
        try {
          const responseRateResponse = await fetch(`${config.api.baseUrl}/cycle-planning/response-rate`, {
            credentials: 'include'
          });
          if (responseRateResponse.ok) {
            const responseRateData = await responseRateResponse.json();
            setResponseRate(responseRateData.response_rate || 0);
          }
        } catch (err) {
          console.error('Error fetching response rate:', err);
        }

        // Fetch cycle data and update submissions with cycle info
        try {
          const cyclesResponse = await fetch(`${config.api.baseUrl}/cycle-planning/cycle-info`, {
            credentials: 'include'
          });
          if (cyclesResponse.ok) {
            const cyclesData = await cyclesResponse.json();
            if (cyclesData.submissions && cyclesData.submissions.length > 0) {
              // Update submissions with cycle information
              const submissionsWithCycles = submissionsData.submissions.map(submission => {
                const cycleSubmission = cyclesData.submissions.find(s => s.submission_id === submission.submission_id);
                return {
                  ...submission,
                  workstream: cycleSubmission?.workstream || 'Unknown',
                  cycle_number: cycleSubmission?.cycle_number || 0
                };
              });
              setAllSubmissions(submissionsWithCycles);
              
              // Extract unique cycles from submissions
              const uniqueCycles = [...new Set(cyclesData.submissions.map(s => 
                `${s.workstream} - Cycle ${s.cycle_number}`
              ))].sort();
              setAllCycles(uniqueCycles);
            } else {
              // Fallback: set submissions without cycle info if cycle data is empty
              console.warn('No cycle data found, using submissions without cycle info');
              setAllSubmissions(submissionsData.submissions || []);
            }
          } else {
            // Fallback: set submissions without cycle info if cycle fetch fails
            console.warn('Cycle data fetch failed, using submissions without cycle info');
            setAllSubmissions(submissionsData.submissions || []);
          }
        } catch (err) {
          console.error('Error fetching cycle data:', err);
          // Fallback: set submissions without cycle info on error
          setAllSubmissions(submissionsData.submissions || []);
        }

        setLoading(false);
      } catch (err) {
        console.error('Dashboard data loading error:', err);
        setError(`Failed to load dashboard data: ${err.message}`);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch filtered cycle planning sessions when program filter changes
  const fetchFilteredProjects = async (programName) => {
    try {
      if (programName) {
        const response = await fetch(`${config.api.baseUrl}/cycle-planning/cp_session?program_name=${encodeURIComponent(programName)}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setFilteredProjects(data.projects || []);
        }
      } else {
        setFilteredProjects(allProjects);
      }
    } catch (err) {
      console.error('Error fetching filtered projects:', err);
      setFilteredProjects(allProjects);
    }
  };

  // Handle program filter changes to fetch filtered cycle planning sessions
  useEffect(() => {
    fetchFilteredProjects(filters.program);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.program, allProjects]);

  // Update AM/PM options when filtered projects change (after program selection)
  useEffect(() => {
    if (filters.program && filteredProjects.length > 0) {
      console.log('Debug: Program selected, filtered projects available:', filteredProjects.length);
      console.log('Debug: Available AMs:', [...new Set(filteredProjects.map(p => p.cycle_planning_am).filter(val => val))]);
      console.log('Debug: Available PMs:', [...new Set(filteredProjects.map(p => p.cycle_planning_pm).filter(val => val))]);
    }
  }, [filteredProjects, filters.program]);

  // Filter the data whenever the filters change.
  useEffect(() => {
    let filteredSubmissions = allSubmissions.filter(s => {
      return (!filters.project || s.cycle_planning_name === filters.project) &&
             (!filters.program || s.program_name === filters.program) &&
             (!filters.am || s.cycle_planning_am === filters.am) &&
             (!filters.pm || s.cycle_planning_pm === filters.pm) &&
             (!filters.account_team || s.account_team === filters.account_team) &&
             (!filters.workstream || s.workstream === filters.workstream) &&
             (!filters.cycle_number || `${s.workstream} - Cycle ${s.cycle_number}` === filters.cycle_number);
    });

    const filteredSubmissionIds = new Set(filteredSubmissions.map(s => s.submission_id));
    const filteredResponses = allResponses.filter(r => filteredSubmissionIds.has(r.submission_id));

    setFilteredData({ submissions: filteredSubmissions, responses: filteredResponses });

    // Calculate filtered response rate using backend
    calculateFilteredResponseRateFromBackend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, allSubmissions, allResponses]);

  // Function to calculate filtered response rate using backend
  const calculateFilteredResponseRateFromBackend = async () => {
    try {
      // Build query parameters from current filters
      const params = new URLSearchParams();
      
      if (filters.project) params.append('cycle_planning_name', filters.project);
      if (filters.program) params.append('program_name', filters.program);
      if (filters.am) params.append('am', filters.am);
      if (filters.pm) params.append('pm', filters.pm);
      if (filters.workstream) params.append('workstream', filters.workstream);
      if (filters.cycle_number) {
        // Extract cycle number from "WS1 - Cycle 1254" format
        const cycleMatch = filters.cycle_number.match(/Cycle (\d+)/);
        if (cycleMatch) {
          params.append('cycle_number', cycleMatch[1]);
        }
      }
      
      const queryString = params.toString();
      const url = `${config.api.baseUrl}/cycle-planning/response-rate${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setFilteredResponseRate(data.response_rate || 0);
      } else {
        console.error('Error fetching filtered response rate:', response.status);
        setFilteredResponseRate(0);
      }
    } catch (err) {
      console.error('Error calculating filtered response rate:', err);
      setFilteredResponseRate(0);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      
      // Clear dependent filters when a parent filter changes
      if (key === 'project') {
        // When session changes, clear program, AM, PM, and account_team
        newFilters.program = '';
        newFilters.am = '';
        newFilters.pm = '';
        newFilters.account_team = '';
      } else if (key === 'program') {
        // When program changes, clear AM, PM, and account_team
        newFilters.am = '';
        newFilters.pm = '';
        newFilters.account_team = '';
      } else if (key === 'am') {
        // When AM changes, clear PM and account_team
        newFilters.pm = '';
        newFilters.account_team = '';
      } else if (key === 'pm') {
        // When PM changes, clear account_team
        newFilters.account_team = '';
      } else if (key === 'workstream') {
        // When workstream changes, clear cycle_number
        newFilters.cycle_number = '';
      }
      // account_team and cycle_number don't clear any other filters
      
      return newFilters;
    });
  };

  const uniqueValues = (key) => {
    // Start with all submissions and apply current filters (except the one we're getting values for)
    let filteredSubmissions = allSubmissions;
    
    // Apply filters dynamically (exclude the current key to avoid circular filtering)
    if (filters.project && key !== 'project') {
      filteredSubmissions = filteredSubmissions.filter(s => s.cycle_planning_name === filters.project);
    }
    if (filters.program && key !== 'program') {
      filteredSubmissions = filteredSubmissions.filter(s => s.program_name === filters.program);
    }
    if (filters.am && key !== 'am') {
      filteredSubmissions = filteredSubmissions.filter(s => s.cycle_planning_am === filters.am);
    }
    if (filters.pm && key !== 'pm') {
      filteredSubmissions = filteredSubmissions.filter(s => s.cycle_planning_pm === filters.pm);
    }
    if (filters.account_team && key !== 'account_team') {
      filteredSubmissions = filteredSubmissions.filter(s => s.account_team === filters.account_team);
    }
    if (filters.workstream && key !== 'workstream') {
      filteredSubmissions = filteredSubmissions.filter(s => s.workstream === filters.workstream);
    }
    if (filters.cycle_number && key !== 'cycle_number') {
      filteredSubmissions = filteredSubmissions.filter(s => `${s.workstream} - Cycle ${s.cycle_number}` === filters.cycle_number);
    }

    if (key === 'project') {
      // Get unique session names from filtered submissions
      const sessionNames = [...new Set(filteredSubmissions.map(s => s.cycle_planning_name).filter(val => val))];
      return sessionNames.sort();
    } else if (key === 'program') {
      // Get unique program names from filtered submissions
      const programNames = [...new Set(filteredSubmissions.map(s => s.program_name).filter(val => val))];
      return programNames.sort();
    } else if (key === 'account_team') {
      // Get unique account teams from filtered submissions
      const accountTeams = [...new Set(filteredSubmissions.map(s => s.account_team).filter(val => val))];
      return accountTeams.sort();
    } else if (key === 'am') {
      // Get unique AM values from filtered submissions
      const amValues = [...new Set(filteredSubmissions.map(s => s.cycle_planning_am).filter(val => val))];
      return amValues.sort();
    } else if (key === 'pm') {
      // Get unique PM values from filtered submissions
      const pmValues = [...new Set(filteredSubmissions.map(s => s.cycle_planning_pm).filter(val => val))];
      return pmValues.sort();
    } else if (key === 'workstream') {
      // Get unique workstreams from filtered submissions
      const workstreams = [...new Set(filteredSubmissions.map(s => s.workstream).filter(val => val))];
      return workstreams.sort();
    } else if (key === 'cycle_number') {
      // Get unique cycles from filtered submissions
      const cycles = [...new Set(filteredSubmissions.map(s => `${s.workstream} - Cycle ${s.cycle_number}`).filter(val => val))];
      return cycles.sort();
    } else {
      // For other fields - get unique values from filtered submissions
      const values = [...new Set(filteredSubmissions.map(s => s[key]).filter(val => val))];
      return values.sort();
    }
  };

  const calculateOverallAverage = () => {
    const validResponses = filteredData.responses.filter(r => !isNaN(parseInt(r.response_value)));
    if (validResponses.length === 0) return "N/A";
    const totalScore = validResponses.reduce((sum, r) => sum + parseInt(r.response_value), 0);
    return (totalScore / validResponses.length).toFixed(2);
  };

  const getCategoryBarChartData = () => {
    const categories = Array.from(new Set(allResponses.map(r => r.question_category))).filter(c => c !== 'Notes');
    const averages = {};
    const backgroundColors = [];
    const borderColors = [];

    categories.forEach(cat => {
      const catScores = filteredData.responses.filter(r => r.question_category === cat && !isNaN(parseInt(r.response_value)));
      if (catScores.length > 0) {
        const total = catScores.reduce((sum, r) => sum + parseInt(r.response_value), 0);
        const avg = parseFloat((total / catScores.length).toFixed(2));
        averages[cat] = avg;

        if (avg < 3.0) {
          backgroundColors.push('#ef4444'); // Red for low scores
          borderColors.push('#ef4444');
        } else if (avg >= 3.0 && avg < 4.0) {
          backgroundColors.push('#eab308'); // Yellow for medium scores
          borderColors.push('#eab308');
        } else {
          backgroundColors.push('#22c55e'); // Green for high scores
          borderColors.push('#22c55e');
        }

      } else {
        averages[cat] = 0;
        backgroundColors.push('rgba(209, 213, 219, 0.8)'); // Gray for no data
        borderColors.push('rgba(156, 163, 175, 1)');
      }
    });

    return {
      labels: categories,
      datasets: [
        {
          label: 'Average Score',
          data: categories.map(cat => averages[cat]),
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
        },
      ],
    };
  };

  const calculateOverTimeData = () => {
    // Get all responses (not just one category) to calculate overall average per submission
    const submissionsWithDate = filteredData.submissions
      .map(s => {
        const dateStr = s.date || s.timestamp;
        if (!dateStr) return null;
        
        const dateObj = new Date(dateStr);
        // Skip if date is invalid
        if (isNaN(dateObj.getTime())) {
          return null;
        }
        
        return {
          ...s,
          date: dateObj
        };
      })
      .filter(s => s !== null) // Remove submissions with invalid dates
      .sort((a, b) => a.date - b.date);

    const dateScores = {};
    submissionsWithDate.forEach(submission => {
      const dateKey = submission.date.toISOString().slice(0, 10);
      if (!dateScores[dateKey]) {
        dateScores[dateKey] = [];
      }
      
      // Get all responses for this submission and calculate average
      const submissionResponses = filteredData.responses.filter(r => 
        r.submission_id === submission.submission_id && !isNaN(parseInt(r.response_value))
      );
      
      if (submissionResponses.length > 0) {
        const total = submissionResponses.reduce((sum, r) => sum + parseInt(r.response_value), 0);
        const average = total / submissionResponses.length;
        dateScores[dateKey].push(average);
      }
    });

    const labels = Object.keys(dateScores).sort();
    const averages = labels.map(date => {
      const scores = dateScores[date];
      if (scores.length > 0) {
        const total = scores.reduce((sum, score) => sum + score, 0);
        return parseFloat((total / scores.length).toFixed(2));
      }
      return 0;
    });

    const result = {
      labels: labels,
      datasets: [
        {
          label: 'Overall Score Over Time',
          data: averages,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.1,
        },
      ],
    };
    
    
    return result;
  };

  const overallAverage = calculateOverallAverage();
  const categoryBarChartData = getCategoryBarChartData();
  const overTimeData = calculateOverTimeData();

  // Calculate summary metrics
  const getSummaryMetrics = () => {
    // Count unique cycle planning + date combinations
    const uniqueSessionDatePairs = new Set(
      filteredData.submissions.map(s => `${s.cycle_planning_name}-${s.date}`)
    ).size;
    
    const uniqueCyclePlannings = new Set(filteredData.submissions.map(s => s.cycle_planning_name)).size; // Unique cycle planning sessions
    const avgScore = parseFloat(overallAverage) || 0;
    const totalSurveys = filteredData.submissions.length;
    
    return {
      totalSessions: uniqueSessionDatePairs, // Unique cycle planning + date pairs
      uniqueCyclePlannings,
      avgScore,
      totalSurveys,
      responseRate: filteredResponseRate // Use the filtered response rate
    };
  };

  const summaryMetrics = getSummaryMetrics();

  // Build data for session averages chart: sessions on X, overall average as single dataset
  const getSessionAveragesData = () => {
    // Sessions shown are from filtered submissions
    const sessions = Array.from(new Set(filteredData.submissions.map(s => s.cycle_planning_name))).filter(Boolean);

    // For each session, compute overall average across all categories
    const sessionData = sessions.map(sessionName => {
      const sessionSubmissionIds = filteredData.submissions
        .filter(s => s.cycle_planning_name === sessionName)
        .map(s => s.submission_id);
      const sessionResponses = filteredData.responses
        .filter(r => sessionSubmissionIds.includes(r.submission_id) && !isNaN(parseInt(r.response_value)))
        .map(r => parseInt(r.response_value));
      if (sessionResponses.length === 0) return 0;
      const avg = sessionResponses.reduce((a, b) => a + b, 0) / sessionResponses.length;
      return parseFloat(avg.toFixed(2));
    });

    // Generate colors based on scores
    const backgroundColors = sessionData.map(score => {
      if (score < 3.0) {
        return '#ef4444'; // Red for low scores
      } else if (score >= 3.0 && score < 4.0) {
        return '#eab308'; // Yellow for medium scores
      } else {
        return '#22c55e'; // Green for high scores
      }
    });

    return {
      labels: sessions,
      datasets: [{
        label: 'Overall Average',
        data: sessionData,
        backgroundColor: backgroundColors
      }]
    };
  };

  // Calculate insights
  const computeCategoryInsights = () => {
    const categories = Array.from(new Set(allResponses.map(r => r.question_category))).filter(c => c !== 'Notes');
    const categoryAverages = {};
    
    categories.forEach(cat => {
      const catScores = filteredData.responses.filter(r => r.question_category === cat && !isNaN(parseInt(r.response_value)));
      if (catScores.length > 0) {
        const total = catScores.reduce((sum, r) => sum + parseInt(r.response_value), 0);
        const avg = parseFloat((total / catScores.length).toFixed(1));
        categoryAverages[cat] = avg;
      }
    });

    const entries = Object.entries(categoryAverages);
    const top = entries.reduce((max, [cat, avg]) => avg > max.avg ? { cat, avg } : max, { cat: '', avg: 0 });
    const bottom = entries.reduce((min, [cat, avg]) => avg < min.avg ? { cat, avg } : min, { cat: '', avg: 5 });

    return { top, bottom };
  };

  const computeProjectInsights = () => {
    // Build project -> scores list using filtered submissions and responses
    const projectScores = {};
    filteredData.submissions.forEach(sub => {
      const project = sub.cycle_planning_name || 'Unknown';
      const responses = filteredData.responses.filter(r => r.submission_id === sub.submission_id && !isNaN(parseInt(r.response_value)));
      if (responses.length > 0) {
        const avg = responses.reduce((sum, r) => sum + parseInt(r.response_value), 0) / responses.length;
        if (!projectScores[project]) projectScores[project] = [];
        projectScores[project].push(avg);
      }
    });

    const projectAverages = Object.entries(projectScores).map(([project, scores]) => {
      const avg = parseFloat((scores.reduce((s, v) => s + v, 0) / scores.length).toFixed(1));
      return { project, avg };
    });

    if (projectAverages.length === 0) {
      return { top: { project: '', avg: 0 }, bottom: { project: '', avg: 0 } };
    }

    const top = projectAverages.reduce((max, p) => p.avg > max.avg ? p : max, projectAverages[0]);
    const bottom = projectAverages.reduce((min, p) => p.avg < min.avg ? p : min, projectAverages[0]);

    return { top, bottom };
  };

  const categoryInsights = computeCategoryInsights();
  const projectInsights = computeProjectInsights();

  // Helper function to get color coding based on score ranges
  const getScoreColor = (score) => {
    if (score < 3) {
      return {
        backgroundColor: '#fef2f2',
        borderColor: '#fecaca',
        textColor: '#dc2626',
        scoreColor: '#dc2626'
      };
    } else if (score >= 3 && score < 4) {
      return {
        backgroundColor: '#fffbeb',
        borderColor: '#fed7aa',
        textColor: '#d97706',
        scoreColor: '#d97706'
      };
    } else {
      return {
        backgroundColor: '#f0fdf4',
        borderColor: '#bbf7d0',
        textColor: '#16a34a',
        scoreColor: '#16a34a'
      };
    }
  };

  // (Removed) Multiselect helper - not needed for dropdowns

  if (loading) {
    return (
      <div style={{ 
        padding: '2rem',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '1.125rem', color: themeColors.secondary }}>Loading dashboard data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '2rem',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '1.125rem', color: themeColors.warning }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '2rem',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      backgroundColor: themeColors.backgroundAlt,
      minHeight: '100vh'
    }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ 
            fontSize: '2rem', 
            fontWeight: '700', 
            color: themeColors.primary, 
            marginBottom: '0.5rem'
          }}>
            Cycle Planning Survey Dashboard
          </h1>
          <p style={{ color: themeColors.secondary, fontSize: '1rem', margin: 0 }}>
            
          </p>
        </div>

        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '1rem', 
          padding: '1.5rem', 
          backgroundColor: themeColors.backgroundAlt, 
          borderRadius: '0.5rem', 
          marginBottom: '2rem',
          border: `1px solid ${themeColors.border}`
        }}>
          
          
          {/* Program Name Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: themeColors.primary }}>
              Program Name:
            </label>
            <select
              onChange={(e) => handleFilterChange('program', e.target.value)}
              value={filters.program}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '0.25rem',
                border: `1px solid ${themeColors.border}`,
                backgroundColor: themeColors.background,
                color: themeColors.primary,
                fontSize: '0.875rem',
                minWidth: '180px',
                outline: 'none',
                transition: 'all 0.2s ease'
              }}
                onFocus={(e) => { e.target.style.borderColor = '#5a8aff'; }}
              onBlur={(e) => { e.target.style.borderColor = themeColors.border; }}
            >
              <option value="">All Programs</option>
              {uniqueValues('program').map(val => <option key={val} value={val}>{val}</option>)}
            </select>
          </div>

          {/* Cycle Planning Session Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: themeColors.primary }}>
              Cycle Planning Session:
            </label>
            <select
              onChange={(e) => handleFilterChange('project', e.target.value)}
              value={filters.project}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '0.25rem',
                border: `1px solid ${themeColors.border}`,
                backgroundColor: themeColors.background,
                color: themeColors.primary,
                fontSize: '0.875rem',
                minWidth: '180px',
                outline: 'none',
                transition: 'all 0.2s ease'
              }}
                onFocus={(e) => { e.target.style.borderColor = '#5a8aff'; }}
              onBlur={(e) => { e.target.style.borderColor = themeColors.border; }}
            >
              <option value="">All Sessions</option>
              {uniqueValues('project').map(val => <option key={val} value={val}>{val}</option>)}
            </select>
          </div>
          {/* AM/PM filters */}
          {true && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: themeColors.primary }}>
                  AM:
                </label>
                <select
                  onChange={(e) => handleFilterChange('am', e.target.value)}
                  value={filters.am}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.25rem',
                    border: `1px solid ${themeColors.border}`,
                    backgroundColor: themeColors.background,
                color: themeColors.primary,
                    fontSize: '0.875rem',
                    minWidth: '120px',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#b6bec6';
                  }}
                >
                  <option value="">All AMs</option>
                  {uniqueValues('am').map(val => <option key={val} value={val}>{val}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: themeColors.primary }}>
                  PM:
                </label>
                <select
                  onChange={(e) => handleFilterChange('pm', e.target.value)}
                  value={filters.pm}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.25rem',
                    border: `1px solid ${themeColors.border}`,
                    backgroundColor: themeColors.background,
                color: themeColors.primary,
                    fontSize: '0.875rem',
                    minWidth: '120px',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#b6bec6';
                  }}
                >
                  <option value="">All PMs</option>
                  {uniqueValues('pm').map(val => <option key={val} value={val}>{val}</option>)}
                </select>
              </div>
            </>
          )}
          {/* Account Team filter temporarily hidden - to be worked on later */}
          {false && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: '600', color: themeColors.primary }}>
                Account Team:
              </label>
              <select
                onChange={(e) => handleFilterChange('account_team', e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.25rem',
                  border: `1px solid ${themeColors.border}`,
                  backgroundColor: themeColors.background,
                color: themeColors.primary,
                  fontSize: '0.875rem',
                  minWidth: '120px',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#b6bec6';
                }}
              >
                <option value="">All Account Teams</option>
                {uniqueValues('account_team').map(val => <option key={val} value={val}>{val}</option>)}
              </select>
            </div>
          )}
          
          {/* Cycle Filters */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: themeColors.primary }}>
              Workstream:
            </label>
            <select
              onChange={(e) => handleFilterChange('workstream', e.target.value)}
              value={filters.workstream}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '0.25rem',
                border: `1px solid ${themeColors.border}`,
                backgroundColor: themeColors.background,
                color: themeColors.primary,
                fontSize: '0.875rem',
                minWidth: '120px',
                outline: 'none',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#b6bec6';
              }}
            >
              <option value="">All Workstreams</option>
              <option value="WS1">WS1</option>
              <option value="WS2">WS2</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: themeColors.primary }}>
              Cycle:
            </label>
            <select
              onChange={(e) => handleFilterChange('cycle_number', e.target.value)}
              value={filters.cycle_number}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '0.25rem',
                border: `1px solid ${themeColors.border}`,
                backgroundColor: themeColors.background,
                color: themeColors.primary,
                fontSize: '0.875rem',
                minWidth: '120px',
                outline: 'none',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#b6bec6';
              }}
            >
              <option value="">All Cycles</option>
              {allCycles.map(cycle => <option key={cycle} value={cycle}>{cycle}</option>)}
            </select>
          </div>
          
          {/* Clear All Filters Button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={() => setFilters({ project: '', program: '', am: '', pm: '', account_team: '', workstream: '', cycle_number: '' })}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.25rem',
                border: '1px solid #dc2626',
                backgroundColor: themeColors.background,
                color: '#dc2626',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#dc2626';
                e.target.style.color = '#ffffff';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = themeColors.background;
                e.target.style.color = '#dc2626';
              }}
            >
              Clear All Filters
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem', 
          marginBottom: '2rem' 
        }}>
          {/* Total Sessions */}
          <div style={{
            backgroundColor: themeColors.background,
            padding: '1.5rem',
            borderRadius: '0.5rem',
            boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${themeColors.border}`,
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: themeColors.textMuted }}>
              Total Sessions
            </h3>
            <p style={{ fontSize: '2rem', fontWeight: '700', margin: 0, lineHeight: 1, color: themeColors.primary }}>
              {summaryMetrics.totalSessions}
            </p>
            <p style={{ fontSize: '0.75rem', color: themeColors.textMuted, margin: '0.25rem 0 0 0' }}>
              Planning sessions tracked across {summaryMetrics.uniqueCyclePlannings} cycle planning(s)
            </p>
          </div>

          {/* Average Score */}
          <div style={{
            backgroundColor: themeColors.background,
            padding: '1.5rem',
            borderRadius: '0.5rem',
            boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${themeColors.border}`,
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: themeColors.textMuted }}>
              Average Score
            </h3>
            <p style={{ fontSize: '2rem', fontWeight: '700', margin: 0, lineHeight: 1, color: themeColors.primary }}>
              {summaryMetrics.avgScore.toFixed(1)}
            </p>
            <p style={{ fontSize: '0.75rem', color: themeColors.textMuted, margin: '0.25rem 0 0 0' }}>
              Across all categories
            </p>
          </div>

          {/* Total Surveys */}
          <div style={{
            backgroundColor: themeColors.background,
            padding: '1.5rem',
            borderRadius: '0.5rem',
            boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${themeColors.border}`,
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: themeColors.textMuted }}>
              Total Surveys
            </h3>
            <p style={{ fontSize: '2rem', fontWeight: '700', margin: 0, lineHeight: 1, color: themeColors.primary }}>
              {summaryMetrics.totalSurveys}
            </p>
            <p style={{ fontSize: '0.75rem', color: themeColors.textMuted, margin: '0.25rem 0 0 0' }}>
              Surveys collected
            </p>
          </div>

          {/* Response Rate */}
          <div style={{
            backgroundColor: themeColors.background,
            padding: '1.5rem',
            borderRadius: '0.5rem',
            boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${themeColors.border}`,
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: themeColors.textMuted }}>
              Response Rate
            </h3>
            <p style={{ fontSize: '2rem', fontWeight: '700', margin: 0, lineHeight: 1, color: themeColors.primary }}>
              {summaryMetrics.responseRate}%
            </p>
            <p style={{ fontSize: '0.75rem', color: themeColors.textMuted, margin: '0.25rem 0 0 0' }}>
              Survey completion
            </p>
          </div>
        </div>

              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '2rem',
                width: '100%',
                maxWidth: '100%'
              }}>

                <div style={{
                  backgroundColor: themeColors.background,
                color: themeColors.primary,
                  padding: '1.5rem',
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                }}>
                  {/* Tab Navigation */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    marginBottom: '1.5rem',
                    borderBottom: '2px solid #b6bec6'
                  }}>
                    <button
                      onClick={() => setActiveTab('overview')}
                      style={{
                        padding: '0.5rem 1.5rem',
                        border: 'none',
                        backgroundColor: activeTab === 'overview' ? '#3b82f6' : 'transparent',
                        color: activeTab === 'overview' ? '#ffffff' : themeColors.secondary,
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        borderTopLeftRadius: '0.375rem',
                        borderTopRightRadius: '0.375rem',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Overview
                    </button>
                    <button
                      onClick={() => setActiveTab('category-insights')}
                      style={{
                        padding: '0.5rem 1.5rem',
                        border: 'none',
                        backgroundColor: activeTab === 'category-insights' ? '#3b82f6' : 'transparent',
                        color: activeTab === 'category-insights' ? '#ffffff' : themeColors.secondary,
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        borderTopLeftRadius: '0.375rem',
                        borderTopRightRadius: '0.375rem',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Category Insights
                    </button>
                    <button
                      onClick={() => setActiveTab('performance-insights')}
                      style={{
                        padding: '0.5rem 1.5rem',
                        border: 'none',
                        backgroundColor: activeTab === 'performance-insights' ? '#3b82f6' : 'transparent',
                        color: activeTab === 'performance-insights' ? '#ffffff' : themeColors.secondary,
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        borderTopLeftRadius: '0.375rem',
                        borderTopRightRadius: '0.375rem',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Performance Insights
                    </button>
                    <button
                      onClick={() => setActiveTab('comments')}
                      style={{
                        padding: '0.5rem 1.5rem',
                        border: 'none',
                        backgroundColor: activeTab === 'comments' ? '#3b82f6' : 'transparent',
                        color: activeTab === 'comments' ? '#ffffff' : themeColors.secondary,
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        borderTopLeftRadius: '0.375rem',
                        borderTopRightRadius: '0.375rem',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Comments & Feedback
                    </button>
                  </div>

                  {/* Tab Content */}
                  {activeTab === 'overview' && (
                    <div style={{ 
                      width: '100%', 
                      display: 'grid', 
                      gridTemplateColumns: '1fr', 
                      gridTemplateRows: 'auto auto auto',
                      gap: '1.5rem' 
                    }}>
                      {/* Category Averages */}
                      <div style={{
                        backgroundColor: themeColors.background,
                color: themeColors.primary,
                        paddingTop: '1.5rem',
                        paddingLeft: '1.5rem',
                        paddingRight: '1.5rem',
                        paddingBottom: '1rem',
                        borderRadius: '0.5rem',
                        border: `1px solid ${themeColors.border}`,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        <h2 style={{ 
                          fontSize: '1.25rem', 
                          fontWeight: '600', 
                          color: themeColors.primary, 
                          margin: 0, 
                          marginBottom: '0.5rem'
                        }}>
                          Category Averages
                        </h2>
                        <SimpleBarChart data={categoryBarChartData} title="Category Averages" themeColors={themeColors} />
                      </div>

                      {/* Score across dates */}
                      <div style={{
                        backgroundColor: themeColors.background,
                color: themeColors.primary,
                        paddingTop: '1.5rem',
                        paddingLeft: '1.5rem',
                        paddingRight: '1.5rem',
                        paddingBottom: '1rem',
                        borderRadius: '0.5rem',
                        border: `1px solid ${themeColors.border}`,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        <h2 style={{ 
                          fontSize: '1.25rem', 
                          fontWeight: '600', 
                          color: themeColors.primary, 
                          margin: 0, 
                          marginBottom: '0.5rem'
                        }}>
                          Score across dates
                        </h2>
                        <SimpleLineChart data={overTimeData} title="Score across dates" themeColors={themeColors} />
                      </div>

                      {/* Session Averages */}
                      <div style={{
                        backgroundColor: themeColors.background,
                color: themeColors.primary,
                        paddingTop: '1.5rem',
                        paddingLeft: '1.5rem',
                        paddingRight: '1.5rem',
                        paddingBottom: '1rem',
                        borderRadius: '0.5rem',
                        border: `1px solid ${themeColors.border}`,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        <h2 style={{ 
                          fontSize: '1.25rem', 
                          fontWeight: '600', 
                          color: themeColors.primary, 
                          margin: 0, 
                          marginBottom: '0.5rem'
                        }}>
                          Session Averages
                        </h2>
                        <SimpleHorizontalBarChart data={getSessionAveragesData()} title="Session Averages" themeColors={themeColors} />
                      </div>

                      {/* Placeholder for 4th div - Hidden for now */}
                      {false && (
                        <div style={{
                          backgroundColor: themeColors.background,
                color: themeColors.primary,
                          paddingTop: '1.5rem',
                          paddingLeft: '1.5rem',
                          paddingRight: '1.5rem',
                          paddingBottom: '1rem',
                          borderRadius: '0.5rem',
                          border: `1px solid ${themeColors.border}`,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <h2 style={{ 
                            fontSize: '1.25rem', 
                            fontWeight: '600', 
                            color: themeColors.textMuted, 
                            margin: 0
                          }}>
                            Coming Soon
                          </h2>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'category-insights' && (
                    <div>
                      <h2 style={{ 
                        fontSize: '1.25rem', 
                        fontWeight: '600', 
                        textAlign: 'center', 
                        color: themeColors.primary, 
                        marginBottom: '1.5rem'
                      }}>
                        Category Performance Insights
                      </h2>

                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                        gap: '1rem' 
                      }}>
                        {/* Category - Top Performer */}
                        {(() => {
                          const topScore = categoryInsights.top.avg || 0;
                          const topColors = getScoreColor(topScore);
                          return (
                            <div style={{
                              backgroundColor: topColors.backgroundColor,
                              border: `1px solid ${topColors.borderColor}`,
                              borderRadius: '0.5rem',
                              padding: '1rem'
                            }}>
                              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: topColors.textColor, marginBottom: '0.5rem' }}>
                                Top Performing Category
                              </h3>
                              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: topColors.scoreColor, marginBottom: '0.5rem' }}>
                                {topScore}/5.0
                              </div>
                              <p style={{ fontSize: '0.75rem', color: themeColors.text, margin: 0 }}>
                                <strong>{categoryInsights.top.cat || 'N/A'}</strong>
                              </p>
                            </div>
                          );
                        })()}

                        {/* Category - Needs Attention */}
                        {(() => {
                          const bottomScore = categoryInsights.bottom.avg || 0;
                          const bottomColors = getScoreColor(bottomScore);
                          return (
                            <div style={{
                              backgroundColor: bottomColors.backgroundColor,
                              border: `1px solid ${bottomColors.borderColor}`,
                              borderRadius: '0.5rem',
                              padding: '1rem'
                            }}>
                              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: bottomColors.textColor, marginBottom: '0.5rem' }}>
                                Category Needs Attention
                              </h3>
                              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: bottomColors.scoreColor, marginBottom: '0.5rem' }}>
                                {bottomScore}/5.0
                              </div>
                              <p style={{ fontSize: '0.75rem', color: themeColors.text, margin: 0 }}>
                                <strong>{categoryInsights.bottom.cat || 'N/A'}</strong>
                              </p>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {activeTab === 'performance-insights' && (
                    <div>
                      <h2 style={{ 
                        fontSize: '1.25rem', 
                        fontWeight: '600', 
                        textAlign: 'center', 
                        color: themeColors.primary, 
                        marginBottom: '1.5rem'
                      }}>
                        Session Performance Insights
                      </h2>

                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                        gap: '1rem' 
                      }}>
                        {/* Project - Top Performer */}
                        {(() => {
                          const topScore = projectInsights.top.avg || 0;
                          const topColors = getScoreColor(topScore);
                          return (
                            <div style={{
                              backgroundColor: topColors.backgroundColor,
                              border: `1px solid ${topColors.borderColor}`,
                              borderRadius: '0.5rem',
                              padding: '1rem'
                            }}>
                              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: topColors.textColor, marginBottom: '0.5rem' }}>
                                Top Performing Session
                              </h3>
                              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: topColors.scoreColor, marginBottom: '0.5rem' }}>
                                {topScore}/5.0
                              </div>
                              <p style={{ fontSize: '0.75rem', color: themeColors.text, margin: 0 }}>
                                <strong>{projectInsights.top.project || 'N/A'}</strong>
                              </p>
                            </div>
                          );
                        })()}

                        {/* Project - Needs Attention */}
                        {(() => {
                          const bottomScore = projectInsights.bottom.avg || 0;
                          const bottomColors = getScoreColor(bottomScore);
                          return (
                            <div style={{
                              backgroundColor: bottomColors.backgroundColor,
                              border: `1px solid ${bottomColors.borderColor}`,
                              borderRadius: '0.5rem',
                              padding: '1rem'
                            }}>
                              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: bottomColors.textColor, marginBottom: '0.5rem' }}>
                                Session Needs Attention
                              </h3>
                              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: bottomColors.scoreColor, marginBottom: '0.5rem' }}>
                                {bottomScore}/5.0
                              </div>
                              <p style={{ fontSize: '0.75rem', color: themeColors.text, margin: 0 }}>
                                <strong>{projectInsights.bottom.project || 'N/A'}</strong>
                              </p>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {activeTab === 'comments' && (
                    <div>
                      <h2 style={{ 
                        fontSize: '1.25rem', 
                        fontWeight: '600', 
                        textAlign: 'center', 
                        color: themeColors.primary, 
                        marginBottom: '1.5rem'
                      }}>
                        Comments & Feedback
                      </h2>

                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '1rem' 
                      }}>
                        {(() => {
                          // Filter comments from responses
                          const comments = filteredData.responses.filter(r => 
                            r.question_category === 'Notes' && 
                            r.response_value && 
                            r.response_value.trim() !== ''
                          );

                          if (comments.length === 0) {
                            return (
                              <div style={{
                                textAlign: 'center',
                                padding: '2rem',
                                color: themeColors.textMuted,
                                fontSize: '1rem'
                              }}>
                                No comments or feedback available yet.
                              </div>
                            );
                          }

                          return comments.map((comment, index) => {
                            // Find the submission for this comment
                            const submission = filteredData.submissions.find(s => 
                              s.submission_id === comment.submission_id
                            );
                            
                            return (
                              <div key={index} style={{
                                backgroundColor: themeColors.backgroundAlt,
                                border: `1px solid ${themeColors.border}`,
                                borderRadius: '0.5rem',
                                padding: '1rem',
                                marginBottom: '0.5rem'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  marginBottom: '0.5rem'
                                }}>
                                  <div>
                                    <h4 style={{
                                      fontSize: '0.875rem',
                                      fontWeight: '600',
                                      color: themeColors.text,
                                      margin: '0 0 0.25rem 0'
                                    }}>
                                      {submission?.cycle_planning_name || 'Unknown Session'}
                                    </h4>
                                    <p style={{
                                      fontSize: '0.75rem',
                                      color: themeColors.textMuted,
                                      margin: 0
                                    }}>
                                      {submission?.date ? new Date(submission.date).toLocaleDateString() : 'Unknown Date'}
                                    </p>
                                  </div>
                                  <div style={{
                                    fontSize: '0.75rem',
                                    color: themeColors.textMuted,
                                    backgroundColor: themeColors.backgroundAlt,
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '0.25rem'
                                  }}>
                                    {submission?.am || 'N/A'} / {submission?.pm || 'N/A'}
                                  </div>
                                </div>
                                <div style={{
                                  fontSize: '0.875rem',
                                  color: themeColors.text,
                                  lineHeight: '1.5',
                                  backgroundColor: themeColors.background,
                                  padding: '0.75rem',
                                  borderRadius: '0.375rem',
                                  border: `1px solid ${themeColors.border}`
                                }}>
                                  {comment.response_value}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
          </div>
  );
};

export default App;
